/* External */
import { useMemo, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

/* Ott-common */
import Card16X9 from '@dtvgo/card-16x9';
import CardContent from '@dtvgo/card-content';
import MainImage from '@dtvgo/main-image';

/* Components */
import { BaseFocusableButton } from '../FocusableButton';
import LiveTimeLeft from '../LiveTimeLeft';
import LiveDetail from '../LiveDetail';

/*  Utils */
import { CardWidth, CardType } from '../../utils/cardsUtils';
import {
  useTitle,
  isPlayingNow,
  isChannel,
  isSchedule,
  isShow,
} from '../../utils/metaDataUtils';
import { useFeaturedCarouselSubtitle } from './utils';
import { ImageRatio } from '../../utils/constants';
import { getIconFromBlockStatus } from '../../utils/iconBlockUtils';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { withCursorVisibilityFilter } from '../../hocs/withCursorVisibilityFilter.hoc';

/*  Hooks */
import useChannel from '../../hooks/useChannel';
import useContent from '../../hooks/useContent';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';
import useImageCard from '../../hooks/useImageCard';

/* Providers */
import { useCurrentTime } from '../../providers/CurrentTimeProvider';

/*  Storages */
import {
  Content, Channel, Show, Schedule,
} from '../../storage/models';

/* Styles */
import './FeaturedCarouselCard.scss';

const IMG_WIDTH = CardWidth[CardType.FEATURED] + 25;
const CARD_DIMENSION = ImageRatio.SIXTEEN_BY_NINE;
const FocusableButton = withCursorVisibilityFilter(BaseFocusableButton);

/*
 * Leaf that owns the minute-tick subscription: the card stays memo-stable
 * while the clock advances; only this badge re-renders (and LiveTimeLeft,
 * which subscribes itself).
 */
function FeaturedCardTimeInfo({ item, showLockIcon, isContentBlockedByParentalControl }) {
  const currentTime = useCurrentTime();

  const isScheduleFlag = isSchedule(item);
  const isPlayingNowFlag = isScheduleFlag && isPlayingNow(item, new Date(currentTime * 60_000));

  const buttonLiveText = useMemo(() => {
    if (showLockIcon || isContentBlockedByParentalControl) {
      if (isScheduleFlag && !isPlayingNowFlag) return 'common.seeChannelLockIcon';
      if (isScheduleFlag && isPlayingNowFlag) return 'common.watchNowLockIcon';
      return 'common.PlayButtonLockIcon';
    }
    if (isShow(item)) return 'slider.seeDetails';
    return isScheduleFlag && !isPlayingNowFlag ? 'live.seeChannel' : 'common.watchNow';
  }, [isScheduleFlag, isPlayingNowFlag, showLockIcon, isContentBlockedByParentalControl, item]);

  return <FormattedMessage id={buttonLiveText} />;
}

FeaturedCardTimeInfo.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Channel),
    PropTypes.instanceOf(Show),
    PropTypes.instanceOf(Schedule),
  ]).isRequired,
  showLockIcon: PropTypes.bool.isRequired,
  isContentBlockedByParentalControl: PropTypes.bool.isRequired,
};

function FeaturedCarouselCard({
  item,
  focused = false,
  active = false,
  onMouseEnter,
  onMouseDown,
}) {
  const content = useContent(isSchedule(item) ? item.content : undefined) || item;

  const { images, description } = content || {};

  const showLockIcon = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const iconName = getIconFromBlockStatus(showLockIcon, isContentBlockedByParentalControl);

  const isScheduleFlag = useMemo(() => isSchedule(item), [item]);

  const title = useTitle(content, item);
  const subtitle = useFeaturedCarouselSubtitle(content, isScheduleFlag);

  const { imageUrl, isDefaultImage } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: CARD_DIMENSION,
  });

  const activeClassName = active ? 'dtv-html5-c-featured-carousel-card--active' : '';

  const assetIsStation = isChannel(item);

  const defaultImageClassName = (isDefaultImage || assetIsStation) ? 'dtv-html5-c-featured-carousel-card--default-image' : '';

  const channelContent = useChannel(isScheduleFlag ? item?.channelId : undefined);

  return (
    <Card16X9
      className={`dtv-html5-c-featured-carousel-card ${activeClassName} ${defaultImageClassName}`}
      onMouseEnter={onMouseEnter}
    >
      {!isDefaultImage && (
        <MainImage
          className={assetIsStation ? 'dtv-html5-c-featured-carousel-card__image--smallImage' : 'dtv-html5-c-featured-carousel-card__image'}
          imageUrl={imageUrl}
          imageAlt={title}
        />
      )}
      <CardContent className="dtv-html5-c-featured-carousel-card__content">
        {isScheduleFlag && (
          <LiveDetail
            startDate={item?.startTime}
            endDate={item?.endTime}
            schedule={item}
          />
        )}
        <CardContent.Title text={title || ''} className="dtv-html5-c-featured-carousel-card__content-title" />
        <CardContent.SubTitle uppercase className="dtv-html5-c-featured-carousel-card__content-subtitle">
          {subtitle}
        </CardContent.SubTitle>
        {isScheduleFlag && (
          <div className="dtv-html5-c-featured-carousel-card__content-schedule">
            <LiveTimeLeft
              startDate={item?.startTime}
              endDate={item?.endTime}
              stationName={channelContent?.title}
              separator="•"
            />
            {channelContent?.title}
          </div>
        )}
        <CardContent.Description text={description || ''} className="dtv-html5-c-featured-carousel-card__content-description" />
        <CardContent.SectionButtons className="dtv-html5-c-featured-carousel-card__content-section-buttons">
          <FocusableButton
            className="dtv-html5-c-featured-carousel-card__button"
            icon={iconName}
            primary
            onMouseDown={onMouseDown}
            focused={focused}
          >
            <FeaturedCardTimeInfo
              item={item}
              showLockIcon={showLockIcon}
              isContentBlockedByParentalControl={isContentBlockedByParentalControl}
            />
          </FocusableButton>
        </CardContent.SectionButtons>
      </CardContent>
    </Card16X9>
  );
}

FeaturedCarouselCard.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Channel),
    PropTypes.instanceOf(Show),
    PropTypes.instanceOf(Schedule),
  ]).isRequired,
  focused: PropTypes.bool,
  active: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseDown: PropTypes.func,
};

export default memo(withInteractiveFocusable()(memo(FeaturedCarouselCard)));
